import cloneDeep from "lodash-es/cloneDeep"

import { createResponseTransformer } from "shared/api/createResponseTransformer"
import { serializer } from "shared/api/serializer/serializer"
import { transformJsonApiResponse } from "shared/api/transformJsonApiResponse"
import { Floor } from "shared/types/Floor"
import { ProductPosition } from "shared/types/ProductPosition"
import {
  FloorTypename,
  ProductPositionTypename,
  Project,
  ProjectChild,
  ProjectsAPIType,
  RoomTypename
} from "shared/types/Project"
import { Room } from "shared/types/Room"

import { transformFloor } from "entities/floors/lib/floorResponseTransformer"
import { transformProductPosition } from "entities/product-positions/graphql/productPositionResponseTransformer"
import { sortProjectChildrenAndUpdateIndex } from "entities/project-children/lib/sortProjectChildren"
import { mapSpecialPriceGroupName } from "entities/projects/graphql/special-price-groups/mapPriceGroupName"
import { transformRoom } from "entities/rooms/lib/roomResponseTransformer"

/**
 * Reads the JSON:API `type` member off a deserialized child.
 *
 * `type` is absent from the generated types on purpose: those model the shape
 * Apollo hands to components, where `__typename` comes from the query. A
 * transformer runs one step earlier, on serializer output, where `type` is what
 * is present and `__typename` is not. Narrowed here rather than widening the
 * whole function to `any`, which is what previously hid this distinction.
 */
function apiTypeOf(child: ProjectChild): string | undefined {
  return (child as { type?: string }).type
}

// Stage-specific counterparts to `isProjectFloor` / `isProjectRoom` /
// `isProjectProductPosition` in `shared/types/Project`, which discriminate on the
// `__typename` that only exists once Apollo has attached it.
function isApiRoom(child: ProjectChild): child is Room {
  return apiTypeOf(child) === RoomTypename
}

function isApiFloor(child: ProjectChild): child is Floor {
  return apiTypeOf(child) === FloorTypename
}

function isApiProductPosition(child: ProjectChild): child is ProductPosition {
  return apiTypeOf(child) === ProductPositionTypename
}

// Generic so the element type survives: a `Floor`'s children go in as `Room[]`
// and come back as `Room[]`, which is what lets `transformFloor` return a `Floor`
// without a cast. Every branch below returns the same shape it received.
export function transformProjectChildren<T extends ProjectChild>(
  children: T[]
): T[]
export function transformProjectChildren(children: null | undefined): []
export function transformProjectChildren<T extends ProjectChild>(
  children: T[] | null | undefined
): T[]
export function transformProjectChildren<T extends ProjectChild>(
  children: T[] | null | undefined
): T[] {
  if (!children) {
    return []
  }

  // Each branch returns the same shape it was given, so the element type is
  // preserved — but the compiler cannot see that through the `ProjectChild` union,
  // hence the one assertion on the result rather than on each branch.
  return children.map(child => {
    if (isApiRoom(child)) {
      return transformRoom(child)
    }
    if (isApiFloor(child)) {
      return transformFloor(child)
    }
    if (isApiProductPosition(child)) {
      return transformProductPosition(child)
    }
    return child
  }) as T[]
}

function transformProject(project: Project | null | undefined): Project | null {
  if (!project) return null

  return {
    ...project,
    specialPriceGroups: project.specialPriceGroups
      ? mapSpecialPriceGroupName(project.specialPriceGroups)
      : null,
    children: project.children
      ? sortProjectChildrenAndUpdateIndex(
          transformProjectChildren(project.children)
        )
      : null
  }
}

export const projectResponseTransformer = createResponseTransformer(json => {
  // json-api-serializer declares `deserialize` as `any`; the project shape is
  // asserted once here so nothing downstream has to.
  const deserialized = cloneDeep(
    serializer.deserialize(ProjectsAPIType, transformJsonApiResponse(json))
  ) as Project | Project[] | null | undefined

  if (Array.isArray(deserialized)) {
    return deserialized.map(project => transformProject(project))
  }

  return transformProject(deserialized)
})
